import * as React from 'react';

import { Breakpoint } from 'types/utils';
import type { HTMLAttributes, CommonlyExcludedHTMLAttributeNames } from 'types/utils';

import { cx } from 'linaria';
import { useOptimizedImages } from 'common/hooks/use-optimized-images';
import { ImagePlaceholder, ImageFallback } from 'common/components';
import { Styles } from './content-blocks.styles';

export namespace ContentBlocks {
  export type BannerImageProps = {
    breakpoint: Breakpoint;
    src: string;
    alt: string;
  } & Omit<HTMLAttributes, CommonlyExcludedHTMLAttributeNames>;

  export const BannerImage: React.FC<BannerImageProps> = ({
    breakpoint,
    src,
    alt,
    className,
    ...htmlProps
  }) => {
    const imgSrc = src.split('?')[0]; // fool-proof removing of image params
    const imgSizes = {
      mobile: {
        w: '768',
        h: '134',
      },
      desktop: {
        w: '940',
        h: '176',
      },
      largeDesktop: {
        w: '1180',
        h: '200',
      },
    };
    const imgOptions = {
      c: 'pad',
      b: 'white',
    };
    const optimizedThumbImage = useOptimizedImages(imgSrc, imgSizes, imgOptions);

    return (
      <picture>
        <source
          media="(max-width: 768px)"
          srcSet={`${optimizedThumbImage.mobile.x} 1x, ${optimizedThumbImage.mobile.xx} 2x`}
        />
        <source
          media="(max-width: 1200px)"
          srcSet={`${optimizedThumbImage.desktop.x} 1x, ${optimizedThumbImage.desktop.xx} 2x`}
        />
        <source
          media="(min-width: 1200px)"
          srcSet={`${optimizedThumbImage.largeDesktop.x} 1x, ${optimizedThumbImage.largeDesktop.xx} 2x`}
        />
        <ImagePlaceholder
          src={optimizedThumbImage.desktop.x || ''}
          loading="lazy"
          alt={alt}
          className={cx(
            Styles.collectionMainPhoto.common,
            Styles.collectionMainPhoto[breakpoint],
            className,
          )}
          fallback={<ImageFallback width={200} height={200} />}
          {...htmlProps}
        />
      </picture>
    );
  };
}
